import { useEffect, useState } from "react";

import type { DocsPage } from "@/presentation/data/docs/docs-content.types";
import { cn } from "@monorepo-apps/ui/src/lib/utils";

interface DocsTocProps {
  page: DocsPage;
}

interface TocEntry {
  id: string;
  text: string;
}

function tocEntries(page: DocsPage): TocEntry[] {
  return page.blocks
    .filter((b): b is Extract<typeof b, { kind: "heading" }> => b.kind === "heading")
    .map((b) => ({ id: b.id, text: b.text }));
}

/** The right column: "On this page" anchors, with scroll-spy on the headings. */
export function DocsToc({ page }: DocsTocProps) {
  const entries = tocEntries(page);
  const activeId = useScrollSpy(entries.map((e) => e.id));

  if (entries.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <p className="eyebrow px-2">On this page</p>
      <ul className="flex flex-col gap-1 border-l border-[var(--border)]">
        {entries.map((entry) => (
          <li key={entry.id}>
            <a
              href={`#${entry.id}`}
              className={cn(
                "-ml-px block border-l px-3 py-0.5 text-xs transition-colors",
                entry.id === activeId
                  ? "border-primary text-primary font-medium"
                  : "text-muted-foreground hover:text-foreground border-transparent"
              )}
            >
              {entry.text}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Tracks which heading is currently nearest the top of the viewport. */
function useScrollSpy(ids: string[]): string | null {
  // Stable key so the effect re-runs when the set of ids changes (page switch).
  const idsKey = ids.join("|");
  const [activeId, setActiveId] = useState<string | null>(ids[0] ?? null);
  // Render-phase reset when the page (and thus its ids) changes — avoids a
  // synchronous setState inside the effect.
  const [seenKey, setSeenKey] = useState(idsKey);
  if (seenKey !== idsKey) {
    setSeenKey(idsKey);
    setActiveId(ids[0] ?? null);
  }

  useEffect(() => {
    const list = idsKey ? idsKey.split("|") : [];
    if (list.length === 0) return;

    const observer = new IntersectionObserver(
      (observed) => {
        const visible = observed.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          // Several headings may intersect in one callback — pick the top-most.
          const topMost = visible.reduce((top, e) =>
            e.boundingClientRect.top < top.boundingClientRect.top ? e : top
          );
          setActiveId(topMost.target.id);
        }
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: 0 }
    );

    for (const id of list) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [idsKey]);

  return activeId;
}
